'use client';
import type { LucideIconName } from '../assets/icons';
import { LUCIDE_ICONS } from '../assets/icons';
import type { SVGAttributes } from 'react';
import { forwardRef } from 'react';

/**
 * Named sizes → px. Kept identical to apps/webapp/src/components/ui/LucideIcon.tsx so the two
 * forks render the same until they are consolidated (SSD-6296 deferred list).
 */
const ICON_SIZE_MAP: Record<string, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 28,
  '2xl': 32,
};

const DEFAULT_ICON_SIZE = 24;

/** Turn `"sm"` into 16, pass numbers/CSS lengths through, fall back to 24 for unknown names. */
const resolveIconSize = (value: number | string | undefined): number | string => {
  if (value === undefined) {
    return DEFAULT_ICON_SIZE;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (value in ICON_SIZE_MAP) {
    return ICON_SIZE_MAP[value];
  }
  // A bare word that is not a known size (e.g. "medium") would become `<svg width="medium">`
  // and throw "Expected length" in the console on every render — the SSD-6304 bug.
  return /^\d/.test(value) ? value : DEFAULT_ICON_SIZE;
};

interface LucideIconProps extends SVGAttributes<SVGSVGElement> {
  name: LucideIconName;
  size?: number | keyof typeof ICON_SIZE_MAP | string;
  /** @deprecated Use className with w-X h-X instead */
  boxSize?: number | string;
}

/**
 * This component is a client component that renders a Lucide icon.
 * It is used to render a Lucide icon in the UI.
 * If you get an error `Only plain objects can be passed to Client Components from Server Components.`,
 * you can use this component instead of the `Icon` component.
 */
export const LucideIcon = forwardRef<SVGSVGElement, LucideIconProps>(
  ({ name, size, boxSize, className, width, height, ...props }, ref) => {
    const LucideIconComponent = LUCIDE_ICONS[name] as React.ElementType;

    if (!LucideIconComponent) {
      console.error(`Icon "${name}" not found in Icons, please add icon in src/assets/icons.ts`);
      return null;
    }

    // Support legacy boxSize prop by converting to width/height.
    // Default to 24px (lucide-react standard) to prevent SVGs from filling container.
    const resolvedSize = resolveIconSize(size ?? boxSize);
    const resolvedWidth = width ?? resolvedSize;
    const resolvedHeight = height ?? resolvedSize;

    return (
      <LucideIconComponent ref={ref} className={className} width={resolvedWidth} height={resolvedHeight} {...props} />
    );
  },
);
